import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  Leaf, Truck, ShieldCheck, Clock, ArrowRight, Upload,
  Sparkles, MapPin, PhoneCall, Star, Package, MessageCircle, Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import QuickPrescriptionCard from "@/components/QuickPrescriptionCard";

/**
 * "Editorial Apothecary" landing template — v2 (2026-02-04)
 * Bolder mixed serif+mono typography, asymmetric grid, running marquee,
 * numbered "chapters" instead of a generic 4-up feature grid, and a big
 * stats band. Location references removed per user request.
 */

// PHARMACY_PHONE is sourced from `frontend/.env` → `REACT_APP_PHARMACY_PHONE`
// so it can be swapped without touching code. Falls back to a placeholder
// when the env var is missing so the chip still renders during local dev.
const PHARMACY_PHONE = process.env.REACT_APP_PHARMACY_PHONE || "9876543210";

const HERO = "https://images.pexels.com/photos/15344167/pexels-photo-15344167.jpeg";
const HERO_2 = "https://images.unsplash.com/photo-1585435557343-3b092031a831?w=900&q=80";

const MARQUEE = [
  "SAME-DAY DELIVERY",
  "★",
  "25,000+ SKUS IN STOCK",
  "★",
  "AI-VERIFIED PRESCRIPTIONS",
  "★",
  "CASH ON DELIVERY · UPI · CARDS",
  "★",
  "PHARMACIST-PACKED, EVERY ORDER",
  "★",
  "ELITE MEMBERS SAVE 20% FLAT",
  "★",
];

const CHAPTERS = [
  {
    n: "I",
    title: "Same-day, seven days a week",
    copy: "Order before 6pm and your medicines land on your doorstep the same evening. Free delivery on orders above ₹500.",
    icon: Truck,
  },
  {
    n: "II",
    title: "Authentic, batch-traced",
    copy: "Every SKU sourced from verified distributors. Batch and expiry printed on every invoice — nothing hidden.",
    icon: ShieldCheck,
  },
  {
    n: "III",
    title: "Prescription, read by AI",
    copy: "Snap your doctor's slip; our AI pharmacist verifies it in seconds and our human pharmacist confirms before dispatch.",
    icon: Sparkles,
  },
  {
    n: "IV",
    title: "Open 8am — 10pm",
    copy: "Late-hours emergency window. Missed a dose? WhatsApp us and we will make it right before bedtime.",
    icon: Clock,
  },
];

const STATS = [
  { k: "27+",     v: "Years serving families" },
  { k: "25,000",  v: "Medicines in stock" },
  { k: "146",     v: "Curated shelves" },
  { k: "4.9★",   v: "Local rating" },
];

const SHELVES = [
  { cat: "pain-relief",           n: "01", label: "Pain Relief",     img: "https://images.unsplash.com/photo-1550572017-edd951b55104?w=1200", span: "lg:col-span-6 lg:row-span-2" },
  { cat: "vitamins",              n: "02", label: "Vitamins",        img: "https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=1200", span: "lg:col-span-3" },
  { cat: "diabetes",              n: "03", label: "Diabetes Care",   img: null, dark: true,   span: "lg:col-span-3" },
  { cat: "cold-flu",              n: "04", label: "Cold & Flu",      img: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=1200", span: "lg:col-span-3" },
  { cat: "first-aid",             n: "05", label: "First Aid",       img: "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=1200", span: "lg:col-span-3" },
  { cat: "chronic",               n: "06", label: "Chronic Care",    img: null, light: true,  span: "lg:col-span-6" },
];

function Marquee() {
  return (
    <div className="border-y border-[#1A362B]/20 bg-[#1A362B] text-[#EFE7D6] overflow-hidden select-none">
      <div className="marquee flex whitespace-nowrap py-3 gap-8">
        {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((m, i) => (
          <span key={i} className="label-caps text-[11px] tracking-[0.25em] text-[#E8C170]/90 shrink-0">
            {m}
          </span>
        ))}
      </div>
    </div>
  );
}

function ContactChip() {
  const phone = PHARMACY_PHONE;
  const [open, setOpen] = useState(false);
  return (
    <div
      className="fixed bottom-5 right-5 z-40 flex items-end gap-3"
      data-testid="landing-contact-strip"
    >
      {/* Expanded rail */}
      <motion.div
        initial={false}
        animate={open ? { opacity: 1, x: 0, pointerEvents: "auto" } : { opacity: 0, x: 16, pointerEvents: "none" }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className="flex items-center gap-2 mb-1"
      >
        <a
          href={`tel:+91${phone}`}
          className="group inline-flex items-center gap-2 pl-3 pr-4 h-11 rounded-full bg-white border border-stone-300 shadow-lg hover:border-[#D96C4A] transition-colors"
          data-testid="contact-chip-call"
        >
          <span className="w-7 h-7 rounded-full bg-[#D96C4A] text-white flex items-center justify-center">
            <Phone className="w-3.5 h-3.5"/>
          </span>
          <span className="label-caps text-[10px] text-[#1A362B] tracking-[0.22em]">Call now</span>
        </a>
        <a
          href={`https://wa.me/91${phone}?text=${encodeURIComponent("Hi Shree Ram Pharma — I need help with an order.")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2 pl-3 pr-4 h-11 rounded-full bg-white border border-stone-300 shadow-lg hover:border-[#25D366] transition-colors"
          data-testid="contact-chip-whatsapp"
        >
          <span className="w-7 h-7 rounded-full bg-[#25D366] text-white flex items-center justify-center">
            <MessageCircle className="w-3.5 h-3.5"/>
          </span>
          <span className="label-caps text-[10px] text-[#1A362B] tracking-[0.22em]">WhatsApp</span>
        </a>
      </motion.div>

      {/* Toggle bubble */}
      <motion.button
        initial={{ opacity: 0, scale: 0.6, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.5, type: "spring", stiffness: 180 }}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close contact options" : "Contact the pharmacy"}
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 ${
          open ? "bg-[#1A362B] text-[#EFE7D6]" : "bg-[#D96C4A] text-white"
        }`}
        data-testid="contact-chip-toggle"
      >
        {open ? (
          <span className="font-serif-display text-2xl leading-none">×</span>
        ) : (
          <span className="relative">
            <MessageCircle className="w-6 h-6"/>
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#25D366] rounded-full ring-2 ring-[#EFE7D6] animate-pulse"/>
          </span>
        )}
      </motion.button>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#EFE7D6] text-[#1A362B]">
      {/* ─── HERO ────────────────────────────────────────────────── */}
      <section className="relative">
        <div className="grid lg:grid-cols-12 min-h-[92vh]">
          {/* Left copy */}
          <div className="lg:col-span-7 flex items-center px-6 lg:px-16 py-16 relative dot-grid">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, ease: "easeOut" }}
              className="max-w-3xl"
            >
              <div className="flex items-center gap-3 mb-8">
                <span className="w-8 h-px bg-[#D96C4A]"/>
                <span className="label-caps text-[#D96C4A] tracking-[0.28em]" data-testid="hero-eyebrow">
                  A neighbourhood chemist · Since 1998
                </span>
              </div>

              <h1 className="font-serif-display font-light tracking-tighter leading-[0.9] text-6xl sm:text-7xl lg:text-[8.5rem] text-[#1A362B]" data-testid="hero-title">
                Shree&nbsp;Ram <br/>
                <span className="italic font-normal text-[#204236]">Pharma.</span>
              </h1>

              <div className="mt-6 max-w-md">
                <p className="font-serif-display italic text-2xl leading-snug text-[#1A362B]/85">
                  A curated apothecary of <span className="text-[#D96C4A] not-italic font-normal">25,000+</span> medicines,
                  <br/>dispensed with the care of a chemist you know.
                </p>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/shop">
                  <Button
                    className="rounded-full bg-[#1A362B] hover:bg-[#204236] text-[#EFE7D6] px-8 h-12 shine active:scale-95 transition-transform group"
                    data-testid="hero-shop-cta"
                  >
                    Browse the shelves
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"/>
                  </Button>
                </Link>
                <a href="#upload-prescription">
                  <Button
                    variant="outline"
                    className="rounded-full border-[#1A362B] bg-transparent text-[#1A362B] hover:bg-[#D96C4A] hover:text-white hover:border-[#D96C4A] px-8 h-12"
                    data-testid="hero-upload-rx-cta"
                  >
                    <Upload className="w-4 h-4 mr-2"/> Send prescription
                  </Button>
                </a>
              </div>

              <div className="mt-14 flex items-center gap-8 text-xs label-caps text-stone-500 flex-wrap">
                <div className="flex items-center gap-2"><Star className="w-3 h-3 text-[#D96C4A] fill-current"/> 4.9 local rating</div>
                <span className="w-1 h-1 rounded-full bg-stone-400"/>
                <div className="flex items-center gap-2"><Package className="w-3 h-3"/> Same-day dispatch</div>
                <span className="w-1 h-1 rounded-full bg-stone-400"/>
                <div className="flex items-center gap-2"><PhoneCall className="w-3 h-3"/> WhatsApp support</div>
              </div>
            </motion.div>
          </div>

          {/* Right editorial image collage */}
          <div className="lg:col-span-5 relative bg-[#1A362B]">
            <img
              src={HERO}
              alt="Apothecary shelves"
              className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0e2419]/70 via-transparent to-[#1A362B]/30"/>

            {/* Corner caption cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="absolute top-8 right-8 border border-[#E8C170]/50 px-4 py-2 backdrop-blur-sm bg-[#0e2419]/40 text-[#E8C170]"
            >
              <div className="label-caps text-[9px] tracking-[0.3em] opacity-70">Plate No.</div>
              <div className="font-serif-display text-3xl leading-none">01</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="absolute bottom-10 left-8 right-8 flex justify-between items-end text-[#EFE7D6] gap-6"
            >
              <div className="max-w-sm">
                <div className="label-caps text-[#E8C170] tracking-[0.3em] text-[10px]">— Featured</div>
                <h3 className="font-serif-display italic text-2xl mt-2 leading-snug">
                  &ldquo;Care wrapped in the wisdom of a chemist you know.&rdquo;
                </h3>
              </div>
              <div className="hidden md:block text-right label-caps text-[10px] opacity-70 leading-relaxed">
                Est.<br/>MCMXCVIII
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── MARQUEE TICKER ─────────────────────────────────────── */}
      <Marquee/>

      {/* ─── CHAPTERS (numbered feature list) ───────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-4">
            <div className="label-caps text-stone-500 tracking-[0.28em] mb-4">— The care manual</div>
            <h2 className="font-serif-display text-5xl lg:text-6xl leading-[0.95] tracking-tight" data-testid="chapters-heading">
              Four small <em className="italic text-[#D96C4A]">promises</em>, kept every order.
            </h2>
          </div>
          <div className="lg:col-span-8 lg:pl-8 lg:border-l lg:border-[#1A362B]/15">
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-14">
              {CHAPTERS.map((c, i) => (
                <motion.div
                  key={c.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                >
                  <div className="flex items-baseline gap-4">
                    <span className="font-serif-display text-4xl text-[#D96C4A]/80 italic">{c.n}.</span>
                    <c.icon className="w-4 h-4 text-[#1A362B]"/>
                  </div>
                  <h4 className="font-serif-display text-2xl text-[#1A362B] mt-3 leading-snug">{c.title}</h4>
                  <p className="mt-3 text-sm text-stone-600 leading-relaxed">{c.copy}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS BAND ──────────────────────────────────────────── */}
      <section className="bg-[#1A362B] text-[#EFE7D6] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #E8C170 0%, transparent 40%), radial-gradient(circle at 85% 60%, #D96C4A 0%, transparent 45%)" }}/>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.v}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="text-center lg:text-left"
              data-testid={`stat-${i}`}
            >
              <div className="font-serif-display font-light text-6xl lg:text-7xl text-[#E8C170] leading-none tracking-tighter">
                {s.k}
              </div>
              <div className="label-caps text-[10px] mt-3 tracking-[0.25em] text-[#EFE7D6]/70">{s.v}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── QUICK PRESCRIPTION UPLOAD ──────────────────────────── */}
      <div id="upload-prescription" className="scroll-mt-20 pt-20 lg:pt-28">
        <QuickPrescriptionCard/>
      </div>

      {/* ─── SHELVES BENTO ───────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="label-caps text-stone-500 tracking-[0.28em]">— Explore</div>
            <h2 className="font-serif-display text-5xl lg:text-6xl text-[#1A362B] tracking-tight mt-3">
              The shelves.
            </h2>
          </div>
          <Link to="/shop" className="label-caps text-sm text-[#D96C4A] hover:text-[#1A362B] transition-colors inline-flex items-center gap-2">
            All 146 shelves <ArrowRight className="w-3.5 h-3.5"/>
          </Link>
        </div>
        <div className="grid lg:grid-cols-12 gap-4 auto-rows-[200px]">
          {SHELVES.map((s) => (
            <Link
              key={s.cat}
              to={`/shop?cat=${s.cat}`}
              className={`${s.span} relative overflow-hidden rounded-md border border-stone-200 group ${s.dark ? "bg-[#1A362B] text-[#EFE7D6] p-6 flex flex-col justify-between" : s.light ? "bg-white p-6 flex flex-col justify-between" : ""}`}
              data-testid={`shelf-${s.cat}`}
            >
              {s.img && (
                <>
                  <img src={s.img} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent"/>
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <div className="label-caps text-[#E8C170] tracking-[0.3em] text-[10px]">{s.n}</div>
                    <h3 className="font-serif-display text-2xl lg:text-3xl mt-1 leading-tight">{s.label}</h3>
                  </div>
                </>
              )}
              {!s.img && (
                <>
                  <div className={`label-caps tracking-[0.3em] text-[10px] ${s.dark ? "text-[#E8C170]" : "text-[#D96C4A]"}`}>{s.n}</div>
                  <div>
                    <h3 className={`font-serif-display text-3xl lg:text-4xl leading-tight ${s.dark ? "text-[#EFE7D6]" : "text-[#1A362B]"}`}>{s.label}</h3>
                    <p className={`text-xs mt-2 ${s.dark ? "text-stone-300" : "text-stone-600"}`}>
                      {s.dark ? "Sugar, insulin & everyday support" : "Regular prescriptions, quietly refilled"}
                    </p>
                  </div>
                </>
              )}
            </Link>
          ))}
        </div>
      </section>

      {/* ─── CLOSING BAND ──────────────────────────────────────── */}
      <section className="bg-[#1A362B] text-[#EFE7D6] relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#D96C4A]/20 blur-3xl"/>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#E8C170]/15 blur-3xl"/>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-caps text-[#E8C170] tracking-[0.3em] mb-5 text-[11px]">— On the shelf, at your door</div>
            <h2 className="font-serif-display text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Two ways to pay.<br/>
              <em className="italic text-[#E8C170]">One promise</em> of care.
            </h2>
            <p className="text-stone-300 mt-7 max-w-md leading-relaxed">
              Choose Cash on Delivery when the courier arrives, or pay securely online with Razorpay.
              Every order is packed by our chemist and dispatched with a live tracker.
            </p>
            <Link to="/shop" className="inline-block mt-8">
              <Button className="rounded-full bg-[#D96C4A] hover:bg-[#E67D5C] text-white h-12 px-9 shine" data-testid="footer-shop-cta">
                Start your order <ArrowRight className="w-4 h-4 ml-2"/>
              </Button>
            </Link>
          </div>

          <div className="grid gap-4">
            <div className="border border-[#EFE7D6]/20 rounded-md p-6 backdrop-blur-sm bg-[#EFE7D6]/[0.03] hover:bg-[#EFE7D6]/[0.06] transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full border border-[#E8C170] text-[#E8C170] flex items-center justify-center font-serif-display text-lg">₹</div>
                <div>
                  <h4 className="font-serif-display text-xl">Cash on Delivery</h4>
                  <p className="text-sm text-stone-300 mt-1">Pay when your medicines reach your doorstep. No account needed.</p>
                </div>
              </div>
            </div>
            <div className="border border-[#EFE7D6]/20 rounded-md p-6 backdrop-blur-sm bg-[#EFE7D6]/[0.03] hover:bg-[#EFE7D6]/[0.06] transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full border border-[#E8C170] text-[#E8C170] flex items-center justify-center font-serif-display">R</div>
                <div>
                  <h4 className="font-serif-display text-xl">Razorpay Online</h4>
                  <p className="text-sm text-stone-300 mt-1">UPI · Cards · NetBanking · Wallets — secure, instant.</p>
                </div>
              </div>
            </div>
            <div className="border border-[#EFE7D6]/10 rounded-md p-6 backdrop-blur-sm bg-[#EFE7D6]/[0.02]">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full border border-[#E8C170]/40 text-[#E8C170]/70 flex items-center justify-center">
                  <Leaf className="w-4 h-4"/>
                </div>
                <div>
                  <h4 className="font-serif-display text-xl text-[#EFE7D6]/90">Loyalty ladder built in</h4>
                  <p className="text-sm text-stone-300 mt-1">10% off ≥ ₹250 · 15% off ≥ ₹1,100 · flat 20% for Elite members.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─────────────────────────────────────────────── */}
      <footer className="border-t border-stone-200 py-10 px-6 lg:px-10 bg-[#EFE7D6]">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-4 text-xs text-stone-500 label-caps tracking-[0.2em]">
          <div className="flex items-center gap-3">
            <span className="font-serif-display text-lg text-[#1A362B] normal-case tracking-normal">Shree Ram Pharma</span>
            <span className="w-1 h-1 rounded-full bg-stone-400"/>
            <span>Est. 1998</span>
          </div>
          <div className="flex items-center gap-5">
            <span>Licence</span>
            <span>Privacy</span>
            <span>Contact</span>
          </div>
        </div>
      </footer>
      <ContactChip/>
    </div>
  );
}
